import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { EventConfig, RsvpCaps, Rsvp } from "@firsttoast/shared";
import { getInvite } from "../api.js";
import { MediaIntro, MediaCard } from "../components/MediaIntro.js";
import { RsvpForm } from "../components/RsvpForm.js";
import { InvalidPage } from "./InvalidPage.js";

type State =
  | { phase: "loading" }
  | { phase: "invalid" }
  | { phase: "ready"; guestName: string; event: EventConfig; caps: RsvpCaps; rsvp?: Rsvp; tableNumber?: string; tablemates?: string[] };

function formatDate(iso?: string) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function TableCard({ tableNumber, tablemates }: { tableNumber: string; tablemates?: string[] }) {
  return (
    <div className="card w-full max-w-sm text-center border-accent/20 bg-accent/5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">Your seat</p>
      <p className="text-3xl font-script text-accent">Table {tableNumber}</p>
      {tablemates && tablemates.length > 0 && (
        <div className="mt-3">
          <p className="text-xs text-muted mb-1">Seated with</p>
          <ul className="space-y-0.5">
            {tablemates.map((name) => (
              <li key={name} className="text-sm font-medium">{name}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<State>({ phase: "loading" });
  const [introDone, setIntroDone] = useState(false);
  const [rsvp, setRsvp] = useState<Rsvp | undefined>();
  const [changing, setChanging] = useState(false);
  const [tableNumber, setTableNumber] = useState<string | undefined>();
  const [tablemates, setTablemates] = useState<string[] | undefined>();
  const rsvpAnchor = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let active = true;
    if (!token) {
      setState({ phase: "invalid" });
      return;
    }
    getInvite(token)
      .then((res) => {
        if (!active) return;
        if (!res.valid) {
          setState({ phase: "invalid" });
          return;
        }
        setState({
          phase: "ready",
          guestName: res.guestName,
          event: res.event,
          caps: res.caps,
          rsvp: res.rsvp,
          tableNumber: res.tableNumber,
          tablemates: res.tablemates,
        });
        setRsvp(res.rsvp);
        setTableNumber(res.tableNumber);
        setTablemates(res.tablemates);
      })
      .catch(() => active && setState({ phase: "invalid" }));
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (introDone) rsvpAnchor.current?.scrollIntoView({ behavior: "smooth" });
  }, [introDone]);

  if (state.phase === "loading") {
    return (
      <main className="grid min-h-dvh place-items-center">
        <div
          className="h-11 w-11 animate-spin rounded-full border-4 border-neutral-200 border-t-accent"
          aria-label="Loading"
        />
      </main>
    );
  }
  if (state.phase === "invalid") return <InvalidPage />;

  // ── RSVP closed: show status (if any) + contact info, no form ───────────
  if (state.event.rsvpClosed) {
    const dateLabel = formatDate(state.event.date);
    return (
      <main className="flex min-h-dvh flex-col lg:h-dvh lg:flex-row">
        {/* Left: event info + status (if responded) + closed notice */}
        <div className="flex w-full flex-col items-center justify-center gap-3 bg-cream px-6 py-12 text-center lg:order-1 lg:w-1/2 lg:px-14">
          <p className="text-xs uppercase tracking-[0.35em] text-accent">You're invited</p>
          <h1 className="font-script text-6xl leading-tight sm:text-7xl">{state.event.title}</h1>
          {dateLabel && <p className="text-lg text-muted">{dateLabel}</p>}
          {state.event.location && <p className="text-lg font-semibold">{state.event.location}</p>}
          {state.event.address && <p className="text-muted">{state.event.address}</p>}

          <div className="my-3 h-px w-16 bg-accent/40" />

          <p className="text-lg">
            Hello, <span className="font-script text-3xl text-accent">{state.guestName}</span>
          </p>

          {rsvp && (
            <div className="card w-full max-w-sm text-center">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                Your response
              </p>
              <h2 className="text-xl font-semibold">
                {rsvp.status === "yes" ? "You're on the list!" : "Response received"}
              </h2>
              {rsvp.status === "yes" ? (
                <p className="mt-2 text-muted">
                  We have you down for {rsvp.adults} adult{rsvp.adults === 1 ? "" : "s"}
                  {rsvp.kids > 0 ? ` and ${rsvp.kids} kid${rsvp.kids === 1 ? "" : "s"}` : ""}.
                  Can't wait to see you!
                </p>
              ) : (
                <p className="mt-2 text-muted">
                  Thank you for letting us know. You'll be missed!
                </p>
              )}
            </div>
          )}

          <div className="card w-full max-w-sm text-center border-amber-200 bg-amber-50">
            <p className="font-semibold text-amber-800">RSVP is now closed</p>
            <p className="mt-1 text-sm text-amber-700">
              To make any changes, please contact your host:
            </p>
            <div className="mt-3 flex flex-col gap-1 text-sm">
              <a href="tel:3304146773" className="font-medium text-accent hover:underline">
                (330) 414-6773
              </a>
              <a href="mailto:m.zonera@gmail.com" className="font-medium text-accent hover:underline">
                m.zonera@gmail.com
              </a>
              <p className="mt-1 text-muted">Zonera Malik</p>
            </div>
          </div>

          {tableNumber && <TableCard tableNumber={tableNumber} tablemates={tablemates} />}
        </div>

        {/* Right: media card */}
        <div className="relative h-[68vh] w-full lg:order-2 lg:h-full lg:w-1/2">
          <MediaCard event={state.event} />
        </div>
      </main>
    );
  }

  // ── Returning guest: status + card split layout ──────────────────────────
  if (rsvp && !changing) {
    const dateLabel = formatDate(state.event.date);
    return (
      <main className="flex min-h-dvh flex-col lg:h-dvh lg:flex-row">
        {/* Left: event details + current RSVP status */}
        <div className="flex w-full flex-col items-center justify-center gap-3 bg-cream px-6 py-12 text-center lg:order-1 lg:w-1/2 lg:px-14">
          <p className="text-xs uppercase tracking-[0.35em] text-accent">You're invited</p>
          <h1 className="font-script text-6xl leading-tight sm:text-7xl">{state.event.title}</h1>
          {dateLabel && <p className="text-lg text-muted">{dateLabel}</p>}
          {state.event.location && <p className="text-lg font-semibold">{state.event.location}</p>}
          {state.event.address && <p className="text-muted">{state.event.address}</p>}

          <div className="my-3 h-px w-16 bg-accent/40" />

          <p className="text-lg">
            Hello, <span className="font-script text-3xl text-accent">{state.guestName}</span>
          </p>

          <div className="card w-full max-w-sm text-center">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
              Your response
            </p>
            <h2 className="text-xl font-semibold">
              {rsvp.status === "yes" ? "You're on the list!" : "Response received"}
            </h2>
            {rsvp.status === "yes" ? (
              <p className="mt-2 text-muted">
                We have you down for {rsvp.adults} adult{rsvp.adults === 1 ? "" : "s"}
                {rsvp.kids > 0 ? ` and ${rsvp.kids} kid${rsvp.kids === 1 ? "" : "s"}` : ""}.
                Can't wait to see you!
              </p>
            ) : (
              <p className="mt-2 text-muted">
                Thank you for letting us know. You'll be missed!
              </p>
            )}
          </div>

          <button className="btn-primary mt-2" onClick={() => setChanging(true)}>
            Change RSVP
          </button>

          {tableNumber && <TableCard tableNumber={tableNumber} tablemates={tablemates} />}
        </div>

        {/* Right: media card */}
        <div className="relative h-[68vh] w-full lg:order-2 lg:h-full lg:w-1/2">
          <MediaCard event={state.event} />
        </div>
      </main>
    );
  }

  // ── Returning guest: RSVP change form (no card) ──────────────────────────
  if (rsvp && changing) {
    return (
      <main>
        <div className="mx-auto max-w-xl px-4 py-10 sm:px-6 sm:py-14">
          <header className="mb-8 text-center">
            <h1 className="font-script text-5xl leading-tight sm:text-6xl">{state.event.title}</h1>
            {state.event.date && (
              <p className="mt-2 text-muted">{formatDate(state.event.date)}</p>
            )}
            {state.event.location && <p className="font-medium">{state.event.location}</p>}
            {state.event.address && <p className="text-sm text-muted">{state.event.address}</p>}
            <p className="mt-4 text-lg">
              Hello, <span className="font-script text-3xl text-accent">{state.guestName}</span>
            </p>
          </header>
          <RsvpForm
            token={token!}
            caps={state.caps}
            existing={rsvp}
            onSaved={(r) => {
              setRsvp(r);
              setChanging(false);
            }}
          />
        </div>
      </main>
    );
  }

  // ── New guest: media intro → RSVP form ───────────────────────────────────
  return (
    <main>
      {!introDone && (
        <MediaIntro
          event={state.event}
          guestName={state.guestName}
          onDone={() => setIntroDone(true)}
        />
      )}

      {introDone && (
        <div ref={rsvpAnchor} className="mx-auto max-w-xl px-4 py-10 sm:px-6 sm:py-14">
          <header className="mb-8 text-center">
            <h1 className="font-script text-5xl leading-tight sm:text-6xl">{state.event.title}</h1>
            {state.event.date && (
              <p className="mt-2 text-muted">{formatDate(state.event.date)}</p>
            )}
            {state.event.location && <p className="font-medium">{state.event.location}</p>}
            {state.event.address && <p className="text-sm text-muted">{state.event.address}</p>}
            <p className="mt-4 text-lg">
              Hello, <span className="font-script text-3xl text-accent">{state.guestName}</span>
            </p>
          </header>
          <RsvpForm
            token={token!}
            caps={state.caps}
            existing={state.rsvp}
            onSaved={setRsvp}
          />
          {rsvp && tableNumber && (
            <div className="mt-6 flex justify-center">
              <TableCard tableNumber={tableNumber} tablemates={tablemates} />
            </div>
          )}
        </div>
      )}
    </main>
  );
}
